import {
  ContextType,
  Dispatch,
  Reducer,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useSyncExternalStore,
  createContext,
} from 'react';
import { canUseScout, claimsOf, session, tokenOf } from 'src/modules/session';

import type { GwscoutJwtPayload } from 'src/modules/session';

import { AuthenticationProvider } from '../modules/generated/api';
import WithChildren from '../types/WithChildren';

// Re-exported for the call sites that have always imported it from here.
export type { GwscoutJwtPayload };

type AuthenticationState = {
  /** The session is active: a token that is present, readable and not expired. */
  hasAccess: boolean;
  /** Active *and* the dealer is permitted to use Scout. A valid token can still be disallowed. */
  canUseScout: boolean;
  simulateDealer: boolean;
  simulateDealerAdmin: boolean;
  simulateCountryManager: boolean;
  accessToken?: string;
  objectToken?: GwscoutJwtPayload;
};

type AuthenticationAction =
  | { type: 'authenticationStore/SIGN_IN'; payload: string }
  | { type: 'authenticationStore/SIGN_OUT' }
  | { type: 'authenticationStore/TOGGLE_SIMULATION_DEALER' }
  | { type: 'authenticationStore/TOGGLE_SIMULATION_COUNTRYMANAGER' }
  | { type: 'authenticationStore/TOGGLE_SIMULATION_DEALERADMIN' }
  | { type: 'authenticationStore/TOGGLE_SIMULATION_OFF' };

export const AuthenticationProviders = {
  grp: 'GRP',
  partnerNet: 'PARTNER_NET',
};

// Thin delegates to the session module, which owns token storage and every auth navigation. Kept
// exported under their old names so the call sites that read a token or build a login url do not
// all have to change at once.
export const getAccessTokenFromStorage = () => session.currentToken();

export const getAuthenticationUrl = (portal?: AuthenticationProvider) => session.getAuthenticationUrl(portal);

/**
 * Returns whether a navigation actually started. It now bounces through the provider decoded off
 * the dying token instead of always landing on the chooser, which is what restores the automatic
 * re-login that the token wipe added in IRIS-6420 had silently removed.
 */
export const redirectToAuthentication = () => session.beginReauthentication();

/**
 * Role simulation is the only thing this store still owns. The session half -- `hasAccess`,
 * `accessToken`, `objectToken` -- is derived from the session module on every render, so the React
 * view and the request-time view cannot drift apart the way they used to.
 */
type SimulationState = Pick<AuthenticationState, 'simulateDealer' | 'simulateCountryManager' | 'simulateDealerAdmin'>;

const initialSimulation: SimulationState = {
  simulateDealer: false,
  simulateCountryManager: false,
  simulateDealerAdmin: false,
};

export const reducer: Reducer<SimulationState, AuthenticationAction> = (state, action) => {
  switch (action.type) {
    case 'authenticationStore/TOGGLE_SIMULATION_DEALER': {
      return { simulateDealer: true, simulateCountryManager: false, simulateDealerAdmin: false };
    }
    case 'authenticationStore/TOGGLE_SIMULATION_DEALERADMIN': {
      return { simulateDealer: false, simulateCountryManager: false, simulateDealerAdmin: true };
    }
    case 'authenticationStore/TOGGLE_SIMULATION_COUNTRYMANAGER': {
      return { simulateDealer: false, simulateCountryManager: true, simulateDealerAdmin: false };
    }
    case 'authenticationStore/TOGGLE_SIMULATION_OFF': {
      return initialSimulation;
    }
    default:
      return state;
  }
};

const initialMapContext: { state: AuthenticationState; dispatch: Dispatch<AuthenticationAction> } = {
  state: { hasAccess: false, canUseScout: false, ...initialSimulation },
  dispatch: () => {},
};
const Store = createContext(initialMapContext);

const Provider = ({ children }: WithChildren) => {
  const sessionState = useSyncExternalStore(session.subscribe, session.getState);
  const [simulation, dispatchSimulation] = useReducer(reducer, initialSimulation);

  // Sign-in and sign-out are storage operations, so they belong to the session module. They keep
  // their action shape so existing dispatchers do not have to change.
  const dispatch = useCallback((action: AuthenticationAction) => {
    switch (action.type) {
      case 'authenticationStore/SIGN_IN':
        session.signIn(action.payload);
        break;
      case 'authenticationStore/SIGN_OUT':
        session.signOut();
        break;
      default:
        dispatchSimulation(action);
    }
  }, []);

  const value = useMemo(
    () => ({
      state: {
        // Note this is stricter than it used to be: an expired token no longer counts as access.
        // That is what stops `useUser` from firing /me on a dead token, which was the trigger for
        // the permanent spinner on /login.
        hasAccess: sessionState.kind === 'active',
        canUseScout: canUseScout(sessionState),
        accessToken: tokenOf(sessionState),
        objectToken: claimsOf(sessionState),
        ...simulation,
      },
      dispatch,
    }),
    [sessionState, simulation, dispatch],
  );

  return <Store.Provider value={value}>{children}</Store.Provider>;
};

export const useAuthenticationStore = (): ContextType<typeof Store> => useContext(Store);

export default Provider;
